import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, XCircle, User, AlertTriangle, Clock, MapPin, Hash } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function AdminRegistrationReview() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);

  const limit = 20;

  useEffect(() => { loadUsers(); }, [page]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/registration-review', { params: { page, limit } });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    setActionLoading(userId);
    try {
      await api.post(`/api/admin/registration-review/${userId}/approve`);
      toast.success('Registration approved');
      loadUsers();
    } catch {
      toast.error('Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Reject this registration?')) return;
    setActionLoading(userId);
    try {
      await api.post(`/api/admin/registration-review/${userId}/reject`);
      toast.success('Registration rejected');
      loadUsers();
    } catch {
      toast.error('Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && users.length === 0) {
    return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading...</div>;
  }

  const getRiskColor = (score) => {
    if (score >= 70) return 'var(--error)';
    if (score >= 40) return 'var(--warning)';
    return 'var(--text-secondary)';
  };

  const getRiskBg = (score) => {
    if (score >= 70) return 'var(--error-bg)';
    if (score >= 40) return 'var(--warning-bg)';
    return 'var(--bg-tertiary)';
  };

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 24px)', maxWidth: 1200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: '1.25rem', fontWeight: 700, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={22} color="var(--accent-primary)" /> Registration Review Queue
          </h2>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>
            {total} flagged registration{total !== 1 ? 's' : ''} pending review
          </p>
        </div>
        <button onClick={loadUsers} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 8,
          border: '1px solid var(--info)40', background: 'transparent',
          color: 'var(--info)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
        }}>Refresh</button>
      </div>

      {users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)', fontSize: 13 }}>
          <Shield size={40} style={{ marginBottom: 16, opacity: 0.3 }} />
          <p>No registrations pending review</p>
        </div>
      ) : (
        <div style={{
          background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
          borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--shadow-sm)',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-primary)', background: 'var(--bg-tertiary)' }}>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Risk Score</th>
                  <th style={thStyle}>IP Info</th>
                  <th style={thStyle}>Registered</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, i) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid var(--border-primary)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-tertiary)' }}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <User size={14} color="var(--text-tertiary)" />
                        <span style={{ fontWeight: 600 }}>{user.full_name || 'Unknown'}</span>
                        {user.company_name && <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>· {user.company_name}</span>}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, fontFamily: 'monospace' }}>{user.email}</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                        background: getRiskBg(user.risk_score), color: getRiskColor(user.risk_score),
                        border: `1px solid ${getRiskColor(user.risk_score)}30`,
                      }}>
                        {user.risk_score || 0}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-tertiary)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MapPin size={10} /> {user.last_ip}
                      </div>
                      {user.ip_registrations > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, color: 'var(--warning)' }}>
                          <AlertTriangle size={10} /> {user.ip_registrations} accounts from this IP
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-tertiary)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Clock size={10} />
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Unknown'}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => handleApprove(user.id)} disabled={actionLoading === user.id}
                          style={actionBtnStyle('var(--success)')} title="Approve">
                          <CheckCircle size={14} /> Approve
                        </button>
                        <button onClick={() => handleReject(user.id)} disabled={actionLoading === user.id}
                          style={actionBtnStyle('var(--error)')} title="Reject">
                          <XCircle size={14} /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
            style={pageBtnStyle(page === 1)}>← Prev</button>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
            style={pageBtnStyle(page >= totalPages)}>Next →</button>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: 'left', padding: '12px 14px', fontSize: 11, fontWeight: 600,
  color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em',
};

const tdStyle = { padding: '12px 14px', fontSize: 13, color: 'var(--text-primary)' };

const actionBtnStyle = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', borderRadius: 8, border: `1px solid ${color}40`,
  background: 'transparent', color, cursor: 'pointer', fontSize: 11, fontWeight: 600,
});

const pageBtnStyle = (disabled) => ({
  padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border-primary)',
  background: 'transparent', color: disabled ? 'var(--text-tertiary)' : 'var(--text-secondary)',
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
  opacity: disabled ? 0.5 : 1,
});
